(() => {
  const PROMO_BAR_NAME = "promo-bar";
  const COUNTDOWN_NAME = "promo-countdown";
  const SLIDE_SELECTOR = "[data-promo-slide]";
  const ACTIVE_CLASS = "is-active";
  const HIDDEN_CLASS = "promo-bar--hidden";
  const CAROUSEL_CLASS = "promo-bar--carousel";
  const MIN_SPEED_SECONDS = 3;

  const getRemainingTime = (endTimestamp) => {
    const remainingMs = endTimestamp * 1000 - Date.now();

    if (remainingMs <= 0) {
      return null;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);

    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  };

  class PromoCountdown extends HTMLElement {
    #intervalId = null;
    #endTimestamp = 0;
    #nodes = {};

    connectedCallback() {
      this.#endTimestamp = Number(this.dataset.end ?? 0);
      this.#nodes = {
        days: this.querySelector("[data-countdown-days]"),
        hours: this.querySelector("[data-countdown-hours]"),
        minutes: this.querySelector("[data-countdown-minutes]"),
        seconds: this.querySelector("[data-countdown-seconds]"),
      };

      this.#update();

      if (this.#intervalId === null && this.isConnected) {
        this.#intervalId = window.setInterval(() => this.#update(), 1000);
      }
    }

    disconnectedCallback() {
      this.#stop();
    }

    #stop() {
      if (this.#intervalId !== null) {
        window.clearInterval(this.#intervalId);
        this.#intervalId = null;
      }
    }

    #update() {
      const remaining = getRemainingTime(this.#endTimestamp);

      if (!remaining) {
        this.#handleExpired();
        return;
      }

      Object.entries(this.#nodes).forEach(([unit, node]) => {
        if (node) node.textContent = String(remaining[unit]);
      });
    }

    #handleExpired() {
      this.#stop();

      const bar = this.closest(PROMO_BAR_NAME);
      const slide = this.closest(SLIDE_SELECTOR);

      if (slide) {
        slide.remove();
      } else {
        this.remove();
      }

      if (typeof bar?.refresh === "function") bar.refresh();
    }
  }

  class PromoBar extends HTMLElement {
    #slides = [];
    #index = 0;
    #timerId = null;
    #pauseReasons = new Set();
    #prevButton = null;
    #nextButton = null;
    #abortController = null;

    connectedCallback() {
      this.#prevButton = this.querySelector("[data-promo-prev]");
      this.#nextButton = this.querySelector("[data-promo-next]");

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#prevButton?.addEventListener("click", () => this.#step(-1), {
        signal,
      });
      this.#nextButton?.addEventListener("click", () => this.#step(1), {
        signal,
      });

      // Autoplay should never pull a message out from under someone reading it,
      // or under a keyboard user tabbing through the link. Each cause holds its
      // own claim, so leaving one does not override another that still stands.
      this.addEventListener("pointerenter", () => this.#pause("pointer"), {
        signal,
      });
      this.addEventListener("pointerleave", () => this.#resume("pointer"), {
        signal,
      });
      this.addEventListener("focusin", () => this.#pause("focus"), { signal });
      this.addEventListener(
        "focusout",
        (event) => {
          // Moving between the arrows and the link is not leaving the bar.
          if (this.contains(event.relatedTarget)) return;
          this.#resume("focus");
        },
        { signal },
      );

      document.addEventListener(
        "visibilitychange",
        () =>
          document.hidden
            ? this.#pause("visibility")
            : this.#resume("visibility"),
        { signal },
      );

      if (window.Shopify?.designMode) {
        document.addEventListener(
          "shopify:block:select",
          (event) => this.#selectBlock(event),
          { signal },
        );
        document.addEventListener(
          "shopify:block:deselect",
          () => this.#resume("editor"),
          { signal },
        );
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.#stopTimer();
      this.#abortController?.abort();
      this.#abortController = null;
    }

    /**
     * Re-reads the slides still in the DOM. Called on connect and whenever an
     * expired countdown removes itself, so the bar collapses to a static
     * message — or disappears entirely — without a page refresh.
     */
    refresh() {
      this.#slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));

      if (this.#slides.length === 0) {
        this.#stopTimer();
        this.classList.add(HIDDEN_CLASS);
        return;
      }

      this.classList.remove(HIDDEN_CLASS);

      const isCarousel = this.#slides.length > 1;
      this.classList.toggle(CAROUSEL_CLASS, isCarousel);

      if (this.#prevButton) this.#prevButton.hidden = !isCarousel;
      if (this.#nextButton) this.#nextButton.hidden = !isCarousel;

      const activeIndex = this.#slides.findIndex((slide) =>
        slide.classList.contains(ACTIVE_CLASS),
      );
      this.#activate(activeIndex === -1 ? 0 : activeIndex);

      // Start rather than resume: an expired countdown must not clear a pause
      // the pointer or focus is still holding.
      if (isCarousel && this.dataset.autoplay === "true") {
        this.#startTimer();
      } else {
        this.#stopTimer();
      }
    }

    #activate(index) {
      if (this.#slides.length === 0) return;

      this.#index = (index + this.#slides.length) % this.#slides.length;

      this.#slides.forEach((slide, slideIndex) => {
        slide.classList.toggle(ACTIVE_CLASS, slideIndex === this.#index);
      });
    }

    #step(direction) {
      this.#activate(this.#index + direction);
      this.#restart();
    }

    #restart() {
      this.#stopTimer();
      this.#startTimer();
    }

    #pause(reason) {
      this.#pauseReasons.add(reason);
      this.#stopTimer();
    }

    #resume(reason) {
      this.#pauseReasons.delete(reason);
      this.#startTimer();
    }

    #stopTimer() {
      if (this.#timerId !== null) {
        window.clearInterval(this.#timerId);
        this.#timerId = null;
      }
    }

    #startTimer() {
      if (this.#timerId !== null) return;
      if (this.#pauseReasons.size > 0) return;
      if (this.#slides.length < 2) return;
      if (this.dataset.autoplay !== "true") return;
      if (document.hidden) return;

      const speed = Math.max(
        Number(this.dataset.speed) || MIN_SPEED_SECONDS,
        MIN_SPEED_SECONDS,
      );

      this.#timerId = window.setInterval(
        () => this.#activate(this.#index + 1),
        speed * 1000,
      );
    }

    #selectBlock(event) {
      const slide = event.target?.closest?.(SLIDE_SELECTOR);
      if (!slide || !this.contains(slide)) return;

      const index = this.#slides.indexOf(slide);
      if (index === -1) return;

      this.#activate(index);
      this.#pause("editor");
    }
  }

  if (!customElements.get(COUNTDOWN_NAME)) {
    customElements.define(COUNTDOWN_NAME, PromoCountdown);
  }

  if (!customElements.get(PROMO_BAR_NAME)) {
    customElements.define(PROMO_BAR_NAME, PromoBar);
  }
})();
